import React, { useContext, useEffect, useState } from "react";
import { RadioButtons, WhiteContainer, Actions, ContentContainer } from "../../../../../styles/orderform.tsx";
import { b2bPreferences } from "../../data.ts";
import { OrderContext } from "../../OrderContext.tsx";
import ClosedWhiteContainer from "../Common/ClosedWhiteContainer.tsx";

import HorizontalLine from "./HorizontalLine.tsx";

import IconRivitalo from '../../../../../assets/images/icon-rivitalo.svg';
import IconOmakotitalo from '../../../../../assets/images/icon-omakotitalo.svg';
import IconKerrostalo from '../../../../../assets/images/icon-kerrostalo.svg';
import IconTehdas2 from '../../../../../assets/images/icon-tehdas2.svg';
import IconInfo from '../../../../../assets/images/icon-info.svg';
import ErrorWrapper from "../Common/ErrorWrapper.tsx";

import { styled } from 'styled-components';
import { bp } from "../../../../../styles/breakPoints.tsx";
import { ValidationErrors } from "../../types.ts";
import { useTranslation } from "react-i18next";

const Estimation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 10px;

  @media ${bp.md} {
    flex-direction: row;
    align-items: flex-end;
    gap: 40px;
  }

  .left {
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 400px;

    @media ${bp.md} {
      justify-content: flex-start;
    }

    input[type=number] {
      width: 100px;
      padding: 10px;
      text-align: center;
    }
  }

  .buttons {
    display: flex;
    gap: 5px;
    align-items: center;
    border: 2px solid #E9ECEE;
    border-radius: 50px;
    font-weight: 500;
    justify-content: space-between;
    width: 100%;

    /* @media ${bp.sm} {
      max-width: 210px;
    } */

    @media ${bp.md} {
      width: 210px;
    }

    button {
      border-radius: 50px;
      border: none;
      background: var(--Lumme);
      color: var(--Valkoinen);
      font-weight: 700;
    }

    input {
      border: none;
      width: 60px;
      padding: 0 0 0 12px;
      text-align: center;
    }
  }

  .range {
    display: flex;
    flex-direction: row;
    margin-bottom: 4px;
    align-items: center;
    gap: 10px;

    @media ${bp.md} {
      display: flex;
      width: 100%;
    }

    span {
      /** keep in one line */
      white-space: nowrap;
      color: var(--Lumme);
    }

    .values {
      display: flex;
      justify-content: space-between;
      padding-bottom: 10px;
    }

    input[type=range] {
      -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
      width: 100%; /* Specific width is required for Firefox. */
      background: transparent; /* Otherwise white in Chrome */
      padding: 12px 0;
    }

    // Chrome track
    input[type=range]::-webkit-slider-runnable-track {
      width: 100%;
      height: 5px;
      border: none;
      box-shadow: none;
      cursor: pointer;
      background: var(--Lumme);
      border-radius: 10px;
    }

    //Chrome thumb
    input[type=range]::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      height: 30px;
      width: 60px;
      background: var(--Lumme);
      background-image: url('https://www.lumme-energia.fi/hubfs/B2C-S%C3%A4hk%C3%B6-Ostoputki/Thumb.svg');
      background-size: cover;
      border-radius: 50px;
      border: none;
      cursor: pointer;
      margin-top: -14px;
    }

    // Firefox track
    input[type=range]::-moz-range-track {
      width: 100%;
      height: 5px;
      border: none;
      box-shadow: none;
      cursor: pointer;
      background: var(--Lumme);
      border-radius: 10px;
    }

    // Firefox thumb
    input[type=range]::-moz-range-thumb {
      height: 30px;
      width: 60px;
      background: var(--Lumme);
      background-image: url('https://www.lumme-energia.fi/hubfs/B2C-S%C3%A4hk%C3%B6-Ostoputki/Thumb.svg');
      background-size: cover;
      border-radius: 50px;
      border: none;
      cursor: pointer;
    }
  }
`;

function PhaseOne() {

  const { formValues, handleFormChange, toPhase } = useContext(OrderContext);
  const { t } = useTranslation();

  const [formErrors, setFormErrors] = useState<ValidationErrors>({});

  const step = 10 * 1000;
  const minValue = 0;
  const maxValue = 500 * 1000;

  const addConsumption = () => {
    const value = parseInt(formValues['consumption-estimate'] || '0')
    if (value >= maxValue) {
      return;
    }
    const data = { name: 'consumption-estimate', value: (value + step).toString() };
    handleFormChange({ target: data } as React.ChangeEvent<HTMLInputElement>);
  }

  const reduceConsumption = () => {
    const value = parseInt(formValues['consumption-estimate'])
    if (value <= minValue) {
      return;
    }
    const data = { name: 'consumption-estimate', value: (value - step).toString() };
    handleFormChange({ target: data } as React.ChangeEvent<HTMLInputElement>);
  }

  const handleConsumptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const val = parseInt(value, 10) * 1000;
    const data = { name: 'consumption-estimate', value: isNaN(val) ? '' : val.toString() };
    handleFormChange({ target: data } as React.ChangeEvent<HTMLInputElement>);
  }

  const [visible, setVisible] = useState<1 | 2 | 3>(1);
  const [errorStyle, setErrorStyle] = useState(false);

  const toNextPhase = () => {
    const errors = validateFields();
    const isValid = Object.keys(errors).length === 0;
    if (!isValid) {
      setErrorStyle(true)
    } else {
      toPhase(2)
    }
  }

  useEffect(() => {
    validateFields();
  }, [formValues]);

  const validateFields = () => {
    const errors: ValidationErrors = {};
    if (!formValues["business-type"]) {
      errors["business-type"] = 'Pakollinen tieto';
    }
    if (!formValues["preferences"]) {
      errors["preferences"] = 'Pakollinen tieto';
    }
    const consumption = parseInt(formValues["consumption-estimate"] || '0');
    if (!formValues["consumption-estimate"]) {
      errors["consumption-estimate"] = 'Pakollinen tieto';
    } else if (!(consumption > minValue && consumption <= maxValue)) {
      errors["consumption-estimate"] = 'Arvo ei ole sallitulla alueella';
    }
    setFormErrors(errors);
    return errors
  }

  const handleClick = (e: React.MouseEvent<HTMLLabelElement|HTMLInputElement>) => {
    const inputName = (e.target as HTMLInputElement).name;
    const isMouseEvent = e.clientX !== 0 || e.clientY !== 0;
    if (!isMouseEvent) {
      return;
    }
    if (inputName === 'business-type') {
      setVisible(2);
      setErrorStyle(false);
    }
    if (inputName === 'preferences') {
      setVisible(3);
      setErrorStyle(false);
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      const inputName = (e.target as HTMLInputElement).name;
      if (inputName === 'business-type') {
        const target = e.target as HTMLInputElement;
        const value = target.value;
        handleFormChange({ target: { name: 'business-type', value } } as React.ChangeEvent<HTMLInputElement>);
        setVisible(2);
        setErrorStyle(false);
      }
      if (inputName === 'preferences') {
        setVisible(3);
        setErrorStyle(false);
      }
    }
  }

  return (
    <>
      {visible === 1 && <ErrorWrapper showError={errorStyle && !!formErrors["business-type"]}>
        <WhiteContainer id="business-type" className={errorStyle && !!formErrors["business-type"] ? 'error' : ''}>
          <h2>{t('phaseOne.chooseBusinessType')}</h2>
          <p>{t('phaseOne.bigCompaniesInfo')}</p>
          <RadioButtons>
            <label className={'radio-label' + (formValues['business-type'] === 'small' ? ' selected' : '')}>
              <input
                type="radio"
                name="business-type"
                value="small"
                checked={formValues['business-type'] === 'small'}
                onChange={handleFormChange}
                onClick={handleClick}
                onKeyDown={handleKeyDown} />
              <div className="image-container">
                <img src={IconRivitalo} alt="Rivitalo" />
                <img src={IconOmakotitalo} alt="Omakotitalo" />
              </div>
              <span className="text">{t('phaseOne.businessOrHousing')} <br /><b>{t('phaseOne.belowkWh')}</b></span>
            </label>
            <a href="https://www.lumme-energia.fi/yritysasiakas-ota-yhteytta" target="_blank" rel="noreferrer" style={{ textAlign: 'center' }}>
              <label className={'radio-label'} >
                <div className="image-container">
                  <img src={IconTehdas2} alt="Tehdas" />
                  <img src={IconKerrostalo} alt="Kerrostalo" />
                </div>
                <span className="text">{t('phaseOne.businessOrHousing')} <br /><b>{t('phaseOne.overkWh')}</b></span>
              </label>
            </a>
          </RadioButtons>
        </WhiteContainer>
      </ErrorWrapper>}

      {visible != 1 && <ClosedWhiteContainer classes={errorStyle && !!formErrors["business-type"] ? 'error' : ''} text={t("phaseOne.businessType")} value={t('phaseOne.smallCompany')} handleClick={() => setVisible(1)} />}

      {visible === 2 && <ErrorWrapper showError={errorStyle && !!formErrors["preferences"]}>
        <WhiteContainer id='preferences' className={errorStyle && !!formErrors["consumption-estimate"] ? 'error' : ''}>
          <h2>{t('phaseOne.wishForElectricity')}</h2>
          <RadioButtons>
            {b2bPreferences.map((option) => (
              <label className={'radio-label' + (formValues['preferences'] === option.value ? ' selected' : '')} htmlFor={option.value} key={option.value}>
                <input
                  type="radio"
                  name="preferences"
                  value={option.value}
                  id={option.value}
                  checked={formValues['preferences'] === option.value}
                  onChange={handleFormChange}
                  onClick={handleClick}
                  onKeyDown={handleKeyDown} />
                <div className='image-container'>
                  <img src={option.img} alt={option.label} />
                </div>
                {t(option.label)}
              </label>
            ))}
          </RadioButtons>

          <div className="info">
            <img src={IconInfo} />
            <p>{t('phaseOne.allContractsWithRenewable')}</p>
          </div>
        </WhiteContainer>
      </ErrorWrapper>}

      {visible !== 2 && <ErrorWrapper showError={errorStyle && !!formErrors["preferences"]}>
        <ClosedWhiteContainer classes={errorStyle && !!formErrors["preferences"] ? 'error' : ''} text={t('phaseOne.wishForElectricity')} value={t(b2bPreferences.find(pref => pref.value === formValues['preferences'])?.label ?? '')} handleClick={() => setVisible(2)} />
      </ErrorWrapper>}

      {visible === 3 && <ErrorWrapper showError={errorStyle && !!formErrors["consumption-estimate"]}>
        <WhiteContainer id='consumption' className={errorStyle && !!formErrors["consumption-estimate"] ? 'error' : ''}>
          <h2>{t('phaseOne.consumptionEstimate')}</h2>
          <div>
            <label>{t('phaseOne.consumptionPerYear')}</label>
            <Estimation>
              <div className="left">
                {/* <div className="buttons"> */}
                {/* <button type="button" onClick={reduceConsumption}>-</button> */}
                <input
                  type="number"
                  placeholder="100 000"
                  min={minValue}
                  max={maxValue}
                  step={step}
                  value={!!formValues["consumption-estimate"] ? parseInt(formValues["consumption-estimate"] || '0') : ''}
                  onChange={handleConsumptionChange} />
                {/* <button type="button" onClick={addConsumption}>+</button> */}
                {/* </div> */}
                <label>kWh</label>
              </div>
              <div className="range">
                <span>0 kWh</span>
                <input
                  type="range"
                  min={minValue}
                  max={maxValue}
                  step={step}
                  value={[formValues["consumption-estimate"] || '0']}
                  onChange={handleFormChange}
                  name="consumption-estimate" />
                <span>500 000 kWh</span>
              </div>

            </Estimation>
          </div>
          <HorizontalLine />

          <div className="info">
            <img src={IconInfo} />
            <p dangerouslySetInnerHTML={{ __html: t('phaseOne.afterAllOver') }}></p>
          </div>

        </WhiteContainer>
      </ErrorWrapper>}

      {visible !== 3 && <ErrorWrapper showError={errorStyle && !!formErrors["consumption-estimate"]}>
        <ClosedWhiteContainer classes={errorStyle && !!formErrors["consumption-estimate"] ? 'error' : ''} text={t('phaseOne.consumptionEstimate')} value={formValues["consumption-estimate"] != '' ? `${parseInt(formValues["consumption-estimate"])} kWh` : ''} handleClick={() => setVisible(3)} />
      </ErrorWrapper>}

      <ContentContainer>
        <p className={"center" + (errorStyle ? ' error' : '')}>{t('phaseOne.toContinue')}</p>
      </ContentContainer>
      <Actions>
        {/* <button type="button" className="primary" disabled={!formValues["consumption-estimate"] || formValues["consumption-estimate"] == '0' || businessType !== 'small'}>Seuraava</button> */}
        <button type="button" className={"primary" + (!!formErrors["consumption-estimate"] || !!formErrors["consumption-estimate"] || formErrors['business-type'] ? ' disabled' : '')} onClick={toNextPhase}>{t('common.next')}</button>
      </Actions>
    </>
  );
}

export default PhaseOne;
